// @flow
import React from 'react';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { Link, withRouter } from 'react-router';
import classnames from 'classnames';
import Modal from './Modal';

import * as uiActions from '../../actions/ui';

import {
  selectPremiumExperiencePreviewsModal,
  selectOpenModalTypeIs,
} from '../../selectors/modals';
import { selectUserIsAuthenticated } from '../../selectors/user';
import { selectIsInApp } from '../../selectors/client';

import Image from '../util/Image';

import stylesFreeCollectionModal from './FreeCollectionStatusModal.css';
import styles from './PremiumExperiencePreviewsModal.css';

import { DEFAULT_PLAN_ID } from '../../constants';
import { SEARCH_IMAGE_DIMS } from '../../lib/imgix';

import AccountJoin from '../messages/AccountJoin';
import AccountLogin from '../messages/AccountLogin';
import { selectUserCanAccessSubscribeView } from '../../selectors/subscription';
import GetPremiumButton from '../common/GetPremiumButton';

type OwnProps = {
  location: Object,
};

type MapStateToProps = {
  isOpen: boolean,
  isAuth: boolean,
  trigger: string,
  isInApp: boolean,
  imageUrl?: string,
  title?: string,
  subtitle?: string,
  plan?: string,
  userCanAccessSubscribeView: boolean,
};

type DispatchProps = {
  hideModal: Function,
};

type Props = OwnProps & MapStateToProps & DispatchProps;

declare var __CAPACITOR__: boolean;

const PremiumExperiencePreviewsModal = ({
  isOpen,
  isAuth,
  hideModal,
  location,
  trigger,
  plan = DEFAULT_PLAN_ID,
  imageUrl,
  title,
  subtitle,
  isInApp,
  userCanAccessSubscribeView,
}: Props) => {
  if (!userCanAccessSubscribeView) {
    return null;
  }

  const onSubscribeClick = () => {
    hideModal();
  };

  const redirectUrl =
    `/${location.pathname}` +
    (location.search
      ? `${location.search}&showSubscribeModal=1&plan=${plan}`
      : `?showSubscribeModal=1&plan=${plan}`);

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={hideModal}
      onClose={hideModal}
      contentLabel="premium-experience-previews-modal"
    >
      <section
        className={stylesFreeCollectionModal.component}
        data-test="premium-experience-previews-modal"
      >
        {imageUrl && (
          <Image
            src={imageUrl}
            width={SEARCH_IMAGE_DIMS.w}
            height={SEARCH_IMAGE_DIMS.h}
            className={styles.img}
            alt=""
          />
        )}
        {title && <h1 className="fz--gamma">{title}</h1>}
        {subtitle && <p className={stylesFreeCollectionModal.subTitle}>{subtitle}</p>}
        <div className={styles.btns}>
          {isAuth ? (
            <GetPremiumButton
              className={classnames(styles.subNowBtn, 'c-btn', 'c-btn--is-purple')}
              onClick={onSubscribeClick}
              trackingContext={{ trigger }}
              targetPlan={plan}
              data-test="premium-experience-previews-modal.sub-now-btn"
            />
          ) : (
            <React.Fragment>
              <Link
                className="c-btn c-btn--is-purple"
                to={isInApp ? '/signup' : `/join?to=${encodeURIComponent(redirectUrl)}`}
                onClick={hideModal}
                data-test="premium-experience-previews-modal.signup-btn"
              >
                <AccountJoin />
              </Link>
              <Link
                className="c-btn c-btn--is-purple"
                to={isInApp ? '/login' : `/login?to=${encodeURIComponent(redirectUrl)}`}
                onClick={hideModal}
                data-test="premium-experience-previews-modal.login-btn"
              >
                <AccountLogin />
              </Link>
            </React.Fragment>
          )}
        </div>
      </section>
    </Modal>
  );
};

function mapStateToProps(state: Object): MapStateToProps {
  const { trigger, title, subtitle, imageUrl, plan } = selectPremiumExperiencePreviewsModal(state);
  return {
    isOpen: selectOpenModalTypeIs(state, 'PREMIUM_EXPERIENCE_PREVIEWS_MODAL'),
    isAuth: selectUserIsAuthenticated(state),
    trigger,
    title,
    subtitle,
    imageUrl,
    plan,
    isInApp: selectIsInApp(state),
    userCanAccessSubscribeView: selectUserCanAccessSubscribeView(state),
  };
}

const dispatchProps: DispatchProps = {
  hideModal: uiActions.hideModal,
};

export default compose(
  withRouter,
  connect(mapStateToProps, dispatchProps)
)(PremiumExperiencePreviewsModal);
